'use client';

import * as React from 'react';

import { Popover as PopoverPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

// Restores wheel AND touch scrolling for a popover that portals to document.body
// while a scroll lock (react-remove-scroll, mounted by vaul's Drawer and by
// modal Dialogs) is active.
//
// The lock's isolation handler cancels any wheel/touchmove event whose target is
// outside its own subtree and isn't registered as a "shard", so an overflowing
// list in a body-portaled popover can never scroll. Radix's Popover does not
// expose the `shards` prop needed to register one, and portaling into the locked
// subtree instead (the previous workaround) makes an `overflow-*` ancestor clip
// the content — the two fixes were mutually exclusive.
//
// Wheel: we scroll the element ourselves and stopPropagation so the event never
// reaches the lock's document-level listener — non-passive because it calls
// preventDefault. Touch: we ONLY stopPropagation (no preventDefault, no manual
// scroll) so the BROWSER scrolls the element natively, keeping momentum — the
// stopPropagation just hides the event from the lock. Doing touch the wheel way
// (manual scroll + preventDefault) would strip inertia from every non-modal
// popover that already scrolls fine. At a scroll boundary we let the event
// bubble so the modal/page behind can't be dragged.
//
// The scroller is resolved from the event target upward rather than assumed to
// be the content element: some popovers scroll on PopoverContent itself (the
// court pickers), others delegate to an inner `CommandList`. No-ops when nothing
// between the target and the popover can scroll, so the page/drawer behind it
// keeps its normal scroll chaining.
function useUnlockedScroll() {
  return React.useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const findScroller = (target: EventTarget | null) => {
      let el = target instanceof Node ? target : null;

      while (el && el !== node.parentNode) {
        if (el instanceof HTMLElement && el.scrollHeight > el.clientHeight) {
          const overflowY = getComputedStyle(el).overflowY;

          if (overflowY === 'auto' || overflowY === 'scroll') return el;
        }

        el = el.parentNode;
      }

      return null;
    };

    const onWheel = (event: WheelEvent) => {
      const scroller = findScroller(event.target);
      if (!scroller) return;

      const atTop = scroller.scrollTop <= 0;
      const atBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

      // Let the boundary case bubble so an over-scroll doesn't feel stuck.
      if ((event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
        return;
      }

      scroller.scrollTop += event.deltaY;
      event.preventDefault();
      event.stopPropagation();
    };

    let lastTouchY: number | null = null;

    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      const scroller = findScroller(event.target);
      if (!scroller) return;

      const y = event.touches[0]?.clientY;
      if (y == null || lastTouchY == null) return;

      // Finger up (y decreasing) scrolls content down: positive delta.
      const deltaY = lastTouchY - y;
      lastTouchY = y;

      // scrollTop is read BEFORE the browser applies this move's scroll, so
      // test the position this move lands on. Reading the current position
      // instead leaves the frame that REACHES the boundary looking in-bounds,
      // so we stopPropagation() one frame too long and a drag leaks to the
      // modal/page behind.
      const nextScrollTop = scroller.scrollTop + deltaY;
      const atTop = nextScrollTop <= 0;
      const atBottom =
        nextScrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

      // At a boundary, let it bubble so the lock stops the modal/page behind
      // from being dragged.
      if ((deltaY < 0 && atTop) || (deltaY > 0 && atBottom)) return;

      // Within bounds: hide the event from the lock's listener and let the
      // browser scroll the element natively (keeps momentum). No preventDefault.
      event.stopPropagation();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
    };
  }, []);
}

const PopoverContent: React.FC<
  React.ComponentProps<typeof PopoverPrimitive.Content> & {
    // Forwarded to the underlying Portal. Pass the surrounding element when the
    // popover lives inside a scroll-locked container (e.g. a vaul Drawer or a
    // modal Dialog): portaling into that subtree keeps the content inside
    // react-remove-scroll's whitelist so its CommandList/scroll area can scroll.
    //
    // Prefer leaving this undefined: the default body portal avoids the
    // clipping that an `overflow-*` ancestor imposes, and scrolling is handled
    // by the wheel handler below.
    container?: React.ComponentProps<
      typeof PopoverPrimitive.Portal
    >['container'];
  }
> = ({ className, align = 'center', sideOffset = 4, container, ...props }) => {
  const scrollRef = useUnlockedScroll();

  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        ref={scrollRef}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 shadow-elevation-lg !z-[10001] w-72 rounded-xl border p-4 outline-hidden',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
};

PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
